const DB_NAME = "elog_offline_v1";
const STORE = "requests";
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function runStore(mode, fn) {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const store = tx.objectStore(STORE);
        const result = fn(store);
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
      })
  );
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.split(",")[1] || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function serializeRequestData(data) {
  if (!(data instanceof FormData)) {
    return { kind: "json", payload: data ?? null };
  }

  const entries = [];
  for (const [name, value] of data.entries()) {
    if (value instanceof Blob) {
      entries.push({
        name,
        type: "file",
        base64: await blobToBase64(value),
        filename: value.name || "upload.bin",
        mime: value.type || "application/octet-stream",
      });
    } else {
      entries.push({ name, type: "text", value: String(value) });
    }
  }
  return { kind: "formData", entries };
}

export function deserializeRequestData(serialized) {
  if (!serialized || serialized.kind === "json") {
    return serialized?.payload ?? serialized;
  }

  const form = new FormData();
  (serialized.entries || []).forEach((entry) => {
    if (entry.type === "file") {
      const binary = atob(entry.base64 || "");
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
      form.append(entry.name, new Blob([bytes], { type: entry.mime }), entry.filename);
    } else {
      form.append(entry.name, entry.value);
    }
  });
  return form;
}

export async function enqueueRequest({ method, url, data, headers }) {
  const serializedData = await serializeRequestData(data);
  const item = {
    method: (method || "POST").toUpperCase(),
    url,
    data: serializedData,
    headers: headers || {},
    created_at: new Date().toISOString(),
    retries: 0,
  };
  await runStore("readwrite", (store) => store.add(item));
  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
  return item;
}

export async function getQueuedRequests() {
  return runStore("readonly", (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function getQueueCount() {
  const rows = await getQueuedRequests();
  return rows.length;
}

export async function clearRequest(id) {
  await runStore("readwrite", (store) => store.delete(id));
  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
}

export async function flushQueue(executor) {
  const rows = await getQueuedRequests();
  if (!rows.length) return { flushed: 0, failed: 0 };

  let flushed = 0;
  let failed = 0;

  for (const row of rows) {
    try {
      const body = deserializeRequestData(row.data);
      await executor({ ...row, body });
      await clearRequest(row.id);
      flushed += 1;
    } catch {
      failed += 1;
    }
  }

  window.dispatchEvent(new CustomEvent("elog-offline-queue-changed"));
  return { flushed, failed };
}
